import { useQuery } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { MetricsChart, ChartDataPoint } from './metrics-chart';
import { metricsService } from '@/services/metrics/metrics.service';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertCircle, Cpu, HardDrive, MemoryStick, Network } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { cn } from '@/lib/utils';

interface HostMetricsPanelProps {
  subscriptionId: string;
  start: string;
  end: string;
  step?: string;
}

export function HostMetricsPanel({
  subscriptionId,
  start,
  end,
  step,
}: HostMetricsPanelProps) {
  // CPU Usage Query
  const { data: cpuData, isLoading: cpuLoading, error: cpuError } = useQuery({
    queryKey: ['metrics', 'cpu', subscriptionId, start, end],
    queryFn: () =>
      metricsService.queryRange({
        subscriptionId,
        query: `100 * (1 - avg without (cpu, mode) (rate(node_cpu_seconds_total{mode="idle"}[5m])))`,
        start,
        end,
        step,
      }),
    enabled: !!subscriptionId && !!start && !!end,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  // Memory Usage Query
  const { data: memoryData, isLoading: memoryLoading, error: memoryError } =
    useQuery({
      queryKey: ['metrics', 'memory', subscriptionId, start, end],
      queryFn: () =>
        metricsService.queryRange({
          subscriptionId,
          query: `100 * (1 - (node_memory_MemAvailable_bytes / node_memory_MemTotal_bytes))`,
          start,
          end,
          step,
        }),
      enabled: !!subscriptionId && !!start && !!end,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  // Disk Usage Query
  const { data: diskData, isLoading: diskLoading, error: diskError } =
    useQuery({
      queryKey: ['metrics', 'disk', subscriptionId, start, end],
      queryFn: () =>
        metricsService.queryRange({
          subscriptionId,
          query: `100 * (1 - (node_filesystem_avail_bytes{mountpoint="/"} / node_filesystem_size_bytes{mountpoint="/"}))`,
          start,
          end,
          step,
        }),
      enabled: !!subscriptionId && !!start && !!end,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  // Network Throughput Query
  const { data: networkData, isLoading: networkLoading, error: networkError } =
    useQuery({
      queryKey: ['metrics', 'network', subscriptionId, start, end],
      queryFn: () =>
        metricsService.queryRange({
          subscriptionId,
          query: `sum(rate(node_network_receive_bytes_total[1m]))`,
          start,
          end,
          step,
        }),
      enabled: !!subscriptionId && !!start && !!end,
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    });

  /**
   * Transform VictoriaMetrics response to chart data points
   * Handles multiple series by aggregating values by timestamp (taking the first non-null value or averaging)
   */
  const transformData = (
    response: any,
    valueKey: string = 'value',
  ): ChartDataPoint[] => {
    // Response structure: { status: "success", data: { resultType: "matrix", result: [...] } }
    if (!response?.data?.result || !Array.isArray(response.data.result) || response.data.result.length === 0) {
      return [];
    }

    // Collect all data points from all series
    const pointsByTimestamp: Map<number, number[]> = new Map();

    for (const series of response.data.result) {
      if (!series.values || !Array.isArray(series.values)) continue;

      for (const [timestamp, valueStr] of series.values) {
        const value = parseFloat(valueStr);
        if (isNaN(value)) continue;

        if (!pointsByTimestamp.has(timestamp)) {
          pointsByTimestamp.set(timestamp, []);
        }
        pointsByTimestamp.get(timestamp)!.push(value);
      }
    }

    // Aggregate values by timestamp (average if multiple series, otherwise use single value)
    const chartData: ChartDataPoint[] = [];
    for (const [timestamp, values] of pointsByTimestamp.entries()) {
      const aggregatedValue = values.length > 1
        ? values.reduce((sum, v) => sum + v, 0) / values.length
        : values[0];

      chartData.push({
        timestamp,
        [valueKey]: aggregatedValue,
      });
    }

    // Sort by timestamp
    return chartData.sort((a, b) => a.timestamp - b.timestamp);
  };

  /**
   * Get the current (latest) value from VictoriaMetrics response
   */
  const getCurrentValue = (data: any): number | null => {
    if (!data?.data?.result || !Array.isArray(data.data.result) || data.data.result.length === 0) {
      return null;
    }

    // Find the latest timestamp across all series
    let latestTimestamp = 0;
    let latestValue: number | null = null;

    for (const series of data.data.result) {
      if (series.value) {
        // Instant query result
        const timestamp = series.value[0];
        const value = parseFloat(series.value[1]);
        if (!isNaN(value) && timestamp > latestTimestamp) {
          latestTimestamp = timestamp;
          latestValue = value;
        }
      } else if (series.values && Array.isArray(series.values) && series.values.length > 0) {
        // Range query result - get the last value
        const lastPoint = series.values[series.values.length - 1];
        const timestamp = lastPoint[0];
        const value = parseFloat(lastPoint[1]);
        if (!isNaN(value) && timestamp >= latestTimestamp) {
          latestTimestamp = timestamp;
          latestValue = value;
        }
      }
    }

    return latestValue;
  };

  const formatPercent = (value: number) => `${value.toFixed(1)}%`;
  const formatBytes = (value: number) => {
    if (value >= 1e9) return `${(value / 1e9).toFixed(2)} GB/s`;
    if (value >= 1e6) return `${(value / 1e6).toFixed(2)} MB/s`;
    if (value >= 1e3) return `${(value / 1e3).toFixed(2)} KB/s`;
    return `${value.toFixed(2)} B/s`;
  };

  const getStatusColor = (value: number, type: 'cpu' | 'memory' | 'disk') => {
    if (type === 'cpu' || type === 'memory') {
      if (value >= 90) return 'text-red-600 dark:text-red-400';
      if (value >= 70) return 'text-yellow-600 dark:text-yellow-400';
      return 'text-green-600 dark:text-green-400';
    }
    if (type === 'disk') {
      if (value >= 90) return 'text-red-600 dark:text-red-400';
      if (value >= 80) return 'text-yellow-600 dark:text-yellow-400';
      return 'text-green-600 dark:text-green-400';
    }
    return 'text-slate-900 dark:text-slate-100';
  };

  const isLoading = cpuLoading || memoryLoading || diskLoading || networkLoading;
  const hasError = cpuError || memoryError || diskError || networkError;

  if (hasError) {
    return (
      <Alert variant="destructive" className="border-red-200 dark:border-red-900">
        <AlertCircle className="h-4 w-4" />
        <AlertDescription>
          Failed to load host metrics. Please try again later.
        </AlertDescription>
      </Alert>
    );
  }

  const metrics = [
    {
      title: 'CPU Usage',
      icon: Cpu,
      color: '#3b82f6',
      bgColor: 'bg-blue-50 dark:bg-blue-950/20',
      iconColor: 'text-blue-600 dark:text-blue-400',
      data: cpuData,
      loading: cpuLoading,
      value: getCurrentValue(cpuData),
      formatter: formatPercent,
      chartKey: 'cpu',
      type: 'cpu' as const,
      description: 'Processor utilization across all cores',
    },
    {
      title: 'Memory Usage',
      icon: MemoryStick,
      color: '#10b981',
      bgColor: 'bg-green-50 dark:bg-green-950/20',
      iconColor: 'text-green-600 dark:text-green-400',
      data: memoryData,
      loading: memoryLoading,
      value: getCurrentValue(memoryData),
      formatter: formatPercent,
      chartKey: 'memory',
      type: 'memory' as const,
      description: 'RAM consumption and availability',
    },
    {
      title: 'Disk Usage',
      icon: HardDrive,
      color: '#f59e0b',
      bgColor: 'bg-amber-50 dark:bg-amber-950/20',
      iconColor: 'text-amber-600 dark:text-amber-400',
      data: diskData,
      loading: diskLoading,
      value: getCurrentValue(diskData),
      formatter: formatPercent,
      chartKey: 'disk',
      type: 'disk' as const,
      description: 'Storage capacity and utilization',
    },
    {
      title: 'Network Throughput',
      icon: Network,
      color: '#8b5cf6',
      bgColor: 'bg-purple-50 dark:bg-purple-950/20',
      iconColor: 'text-purple-600 dark:text-purple-400',
      data: networkData,
      loading: networkLoading,
      value: getCurrentValue(networkData),
      formatter: formatBytes,
      chartKey: 'network',
      type: 'cpu' as const,
      description: 'Network receive throughput',
    },
  ];

  return (
    <div className="space-y-6">
      <div className="mb-6">
        <h2 className="text-2xl font-bold tracking-tight mb-2">Host Metrics</h2>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          System resource utilization and performance metrics
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {metrics.map((metric) => {
          const Icon = metric.icon;
          const statusColor = metric.value !== null ? getStatusColor(metric.value, metric.type) : 'text-slate-900 dark:text-slate-100';

          return (
            <Card key={metric.title} className="border-slate-200 dark:border-slate-800 shadow-sm hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={cn('p-2 rounded-lg', metric.bgColor)}>
                      <Icon className={cn('h-5 w-5', metric.iconColor)} />
                    </div>
                    <div>
                      <CardTitle className="text-lg font-semibold">{metric.title}</CardTitle>
                      <CardDescription className="text-xs mt-1">{metric.description}</CardDescription>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {metric.loading ? (
                  <div className="space-y-3">
                    <Skeleton className="h-12 w-32" />
                    <Skeleton className="h-[320px] w-full" />
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-baseline gap-2">
                      <span className={cn('text-4xl font-bold tracking-tight', statusColor)}>
                        {metric.value !== null ? metric.formatter(metric.value) : 'N/A'}
                      </span>
                    </div>
                    <div className="mt-4">
                      <MetricsChart
                        data={transformData(metric.data, metric.chartKey)}
                        lines={[
                          {
                            key: metric.chartKey,
                            name: metric.title,
                            color: metric.color
                          },
                        ]}
                        yAxisLabel={metric.title.includes('Network') ? 'Bytes/s' : 'Usage %'}
                        valueFormatter={metric.formatter}
                        showArea={true}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

